import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Activity,
  ArrowRight,
  BookOpen,
  CalendarDays,
  Clock,
  Contact,
  Inbox,
  Mail,
  MailCheck,
  UserCircle,
  UserPlus,
  Users,
} from "lucide-react";

import { formatTime } from "@/routes/events.index";
import { AppShell } from "@/components/app/app-shell";
import { LogHoursDialog } from "@/components/app/log-hours-dialog";
import { Button } from "@/components/ui/button";
import {
  activityQuery,
  contactsQuery,
  conversationsQuery,
  myContextQuery,
  myShiftsQuery,
  recruitSummaryQuery,
  sendsQuery,
  timeSummaryQuery,
} from "@/lib/queries";
import { cancelMyShift } from "@/lib/events.functions";
import { claimFirstAdmin } from "@/lib/volunteer.functions";
import { reportHandledSync } from "@/lib/observability";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Volunteer Dashboard | Warrior Volunteer" },
      { name: "description", content: "Volunteer Dashboard in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Volunteer Dashboard | Warrior Volunteer" },
      { property: "og:description", content: "Volunteer Dashboard in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DashboardPage,
});

const SECTION_LABELS: Record<string, string> = {
  outreach: "Inbox",
  recruiting: "Recruiting",
  contacts: "Contacts",
  events: "Events",
  kits: "Enablement kits",
  admin: "Admin",
  other: "Other",
};

function DashboardPage() {
  const { data: me } = useQuery(myContextQuery);
  const { data: contacts = [] } = useQuery(contactsQuery);
  const { data: sends = [] } = useQuery(sendsQuery);
  const { data: activity = [] } = useQuery(activityQuery);
  const { data: shifts = [] } = useQuery(myShiftsQuery);
  const { data: time } = useQuery({ ...timeSummaryQuery, retry: false });
  const { data: recruit } = useQuery({ ...recruitSummaryQuery, retry: false });
  const { data: inbox } = useQuery({ ...conversationsQuery("mine"), retry: false });
  const cancelShift = useServerFn(cancelMyShift);
  const claim = useServerFn(claimFirstAdmin);
  const queryClient = useQueryClient();

  const totalHours =
    time?.totals.all ?? activity.reduce((sum, row) => sum + Number(row.hours ?? 0), 0);
  const noRoles = me && me.roles.length === 0;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (shifts as any[])
    .filter((s) => (s.event_slots?.event_dates?.event_date ?? "") >= today)
    .sort((a, b) =>
      (a.event_slots?.event_dates?.event_date ?? "").localeCompare(
        b.event_slots?.event_dates?.event_date ?? "",
      ),
    );

  const threads = (inbox?.conversations ?? []).filter((c: any) => c.box === "open");
  const needsReply = threads.filter((c: any) => c.state === "needs_reply");
  const unread = threads.filter((c: any) => c.unread).length;

  const invites = recruit?.invites ?? [];
  const invitesJoined = invites.filter((i: any) => i.status === "converted").length;

  async function handleClaim() {
    try {
      const result = await claim({});
      if (result.claimed) {
        toast.success("You are now the admin for this workspace.");
        queryClient.invalidateQueries();
      } else {
        toast.error("An admin already exists. Ask them to grant you access.");
      }
    } catch {
      toast.error("Could not claim admin access.");
    }
  }

  return (
    <AppShell
      eyebrow="Warrior Volunteer"
      title={`Welcome back${me?.profile?.full_name ? `, ${me.profile.full_name.split(" ")[0]}` : ""}`}
    >
      {noRoles ? (
        <div className="panel mb-6 border-cyan/40 p-6">
          <h2 className="t-h3 text-ice">Your account is not linked to a volunteer role yet</h2>
          <p className="t-body mt-2 text-sm">
            If you are setting this workspace up for the first time, claim admin access. Otherwise
            an admin needs to approve your application.
          </p>
          <div className="mt-4 flex gap-3">
            <Button onClick={handleClaim}>Claim admin access</Button>
            <Link
              to="/profile"
              className="inline-flex items-center rounded-sm border border-border px-4 py-2 text-sm font-semibold text-ice/80 hover:border-cyan hover:text-cyan"
            >
              Fill in my profile
            </Link>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Stat label="Hours logged" value={totalHours} icon={Clock} to="/time" />
        <Stat label="Upcoming shifts" value={upcoming.length} icon={CalendarDays} to="/calendar" />
        <Stat label="Needs reply" value={needsReply.length} icon={Inbox} to="/outreach" />
        <Stat label="Unread replies" value={unread} icon={MailCheck} to="/outreach" />
        <Stat label="Contacts" value={contacts.length} icon={Users} to="/contacts" />
        <Stat label="People invited" value={invites.length} icon={UserPlus} to="/recruit" />
      </div>

      <div className="panel mt-4 flex flex-col items-start justify-between gap-3 p-5 sm:flex-row sm:items-center">
        <div>
          <p className="font-semibold text-ice">Worked outside the app?</p>
          <p className="t-meta mt-0.5">
            Log your hours here so they count toward your totals.
          </p>
        </div>
        <LogHoursDialog />
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <section className="panel p-6">
          <div className="flex items-center justify-between">
            <h2 className="t-h3 text-ice">Your next shifts</h2>
            <Link to="/events" className="text-[0.7rem] font-semibold tracking-wide text-cyan">
              Find a shift
            </Link>
          </div>
          {upcoming.length ? (
            <ul className="mt-4 space-y-3">
              {upcoming.slice(0, 5).map((shift: any) => {
                const slot = shift.event_slots;
                const date = slot?.event_dates;
                const event = date?.events;
                return (
                  <li
                    key={shift.id}
                    className="flex flex-wrap items-start justify-between gap-3 border-l-2 border-cyan/70 pl-3"
                  >
                    <div>
                      <p className="text-sm font-semibold text-ice">
                        {event?.title ?? "Event"} · {slot?.title}
                      </p>
                      <p className="t-meta mt-1">
                        {date?.event_date
                          ? new Date(`${date.event_date}T12:00:00`).toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })
                          : ""}
                        {slot?.start_time ? ` · ${formatTime(slot.start_time)}` : ""}
                        {event?.location_name ? ` · ${event.location_name}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="text-xs font-semibold text-muted-foreground hover:text-destructive"
                      onClick={async () => {
                        if (!confirm("Release this spot?")) return;
                        try {
                          await cancelShift({ data: { id: shift.id } });
                          toast.success("Spot released.");
                          queryClient.invalidateQueries({ queryKey: ["shifts", "mine"] });
                        } catch (error) {
                          reportHandledSync(error, { feature: "dashboard", action: "releaseShift" });
                          toast.error(
                            error instanceof Error ? error.message : "Could not release that spot.",
                          );
                        }
                      }}
                    >
                      Release
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="t-body mt-3 text-sm">
              No shifts claimed yet. Browse the events board and grab a slot that fits your
              schedule.
            </p>
          )}
          <Link
            to="/calendar"
            className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan"
          >
            Open my calendar <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        <section className="panel p-6">
          <div className="flex items-center justify-between">
            <h2 className="t-h3 text-ice">Inbox</h2>
            <Link to="/outreach" className="text-[0.7rem] font-semibold tracking-wide text-cyan">
              Open inbox
            </Link>
          </div>
          {threads.length ? (
            <ul className="mt-4 space-y-3">
              {(needsReply.length ? needsReply : threads).slice(0, 5).map((thread: any) => (
                <li key={thread.thread_id} className="border-l-2 border-cyan/70 pl-3">
                  <p className="text-sm font-semibold text-ice">
                    {thread.contact_name ??
                      [thread.first_name, thread.last_name].filter(Boolean).join(" ") ??
                      thread.email}
                    {thread.unread ? (
                      <span className="ml-2 rounded-sm bg-cyan/20 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-cyan">
                        New
                      </span>
                    ) : null}
                  </p>
                  <p className="t-meta mt-1 line-clamp-1">
                    {thread.last_subject}
                    {thread.days_waiting ? ` · waiting ${thread.days_waiting}d` : ""}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="t-body mt-3 text-sm">
              No conversations yet. Add a contact, then start a message with an enablement kit
              template.
            </p>
          )}
        </section>
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        <section className="panel p-6">
          <div className="flex items-center justify-between">
            <h2 className="t-h3 text-ice">Time tracking</h2>
            <Clock className="h-4 w-4 text-cyan" />
          </div>
          <p className="mt-3 text-3xl font-bold tabular-nums text-ice">{totalHours}</p>
          <p className="t-meta mt-1">total hours volunteered</p>
          <dl className="mt-4 space-y-2 text-sm">
            <Row label="Event shifts" value={`${time?.totals.event ?? 0} hr`} />
            <Row label="In app" value={`${time?.totals.app ?? 0} hr`} />
            <Row label="Manual entries" value={`${time?.totals.manual ?? 0} hr`} />
          </dl>
          {time?.sections?.length ? (
            <p className="t-meta mt-3">
              Most app time in {SECTION_LABELS[time.sections[0]!.section] ?? time.sections[0]!.section}
            </p>
          ) : null}
          <Link
            to="/time"
            className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan"
          >
            See my time <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        <section className="panel p-6">
          <div className="flex items-center justify-between">
            <h2 className="t-h3 text-ice">Recruiting</h2>
            <UserPlus className="h-4 w-4 text-cyan" />
          </div>
          <div className="mt-3 flex gap-8">
            <div>
              <p className="text-3xl font-bold tabular-nums text-ice">{invites.length}</p>
              <p className="t-meta mt-1">invited</p>
            </div>
            <div>
              <p className="text-3xl font-bold tabular-nums text-ice">{invitesJoined}</p>
              <p className="t-meta mt-1">joined</p>
            </div>
          </div>
          <p className="t-body mt-4 text-sm">
            Add people you know to the invite funnel. They get a warm first email that names you,
            then follow ups until they sign up.
          </p>
          <Link
            to="/recruit"
            className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan"
          >
            Invite someone <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        <section className="panel p-6">
          <div className="flex items-center justify-between">
            <h2 className="t-h3 text-ice">Your programs</h2>
            <BookOpen className="h-4 w-4 text-cyan" />
          </div>
          {me?.assignments.length ? (
            <ul className="mt-4 space-y-3">
              {me.assignments.map((assignment) => (
                <li key={assignment.id} className="border-l-2 border-cyan/70 pl-3">
                  <p className="text-sm font-semibold text-ice">{assignment.programs?.name}</p>
                  <p className="t-meta mt-1">{assignment.role_on_program ?? "Volunteer"}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="t-body mt-3 text-sm">
              No program assignments yet. You can still browse every enablement kit: emails, call
              scripts, text messages and one pagers.
            </p>
          )}
          <Link
            to="/kits"
            className="mt-5 inline-flex items-center gap-2 text-[0.7rem] font-semibold tracking-wide text-cyan"
          >
            Open enablement kits <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>
      </div>

      <section className="panel mt-8 p-6">
        <h2 className="t-h3 text-ice">Recent sends</h2>
        {sends.length ? (
          <ul className="mt-4 space-y-3">
            {sends.slice(0, 5).map((send) => (
              <li key={send.id} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-ice">{send.subject}</p>
                  <p className="t-meta mt-1">
                    {send.outreach_contacts?.contact_name ?? "Contact"}
                    {send.programs?.name ? ` · ${send.programs.name}` : ""}
                  </p>
                </div>
                <span className="t-meta shrink-0">
                  {new Date(send.sent_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="t-body mt-3 text-sm">
            Nothing sent yet. Add a contact, then send a kit email.
          </p>
        )}
      </section>

      <section className="mt-8">
        <h2 className="t-h3 text-ice">Everything you can do here</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            to="/events"
            icon={CalendarDays}
            title="Events board"
            body="Claim a shift at an upcoming event and add it to your calendar."
          />
          <Tile
            to="/outreach"
            icon={Mail}
            title="Inbox"
            body="Send kit emails with your signature and handle replies in one thread."
          />
          <Tile
            to="/contacts"
            icon={Contact}
            title="Contacts"
            body="Track organizations, notes and every touch, and share contacts with teammates."
          />
          <Tile
            to="/kits"
            icon={BookOpen}
            title="Enablement kits"
            body="Ready made emails, call scripts, text messages and one pagers per program."
          />
          <Tile
            to="/recruit"
            icon={UserPlus}
            title="Recruit volunteers"
            body="Invite people you know and see who joined from your invites."
          />
          <Tile
            to="/time"
            icon={Clock}
            title="Time tracking"
            body="Event hours file themselves and app time is logged by what you work on."
          />
          <Tile
            to="/activity"
            icon={Activity}
            title="Activity log"
            body="Add hours by hand for anything that happened outside the app."
          />
          <Tile
            to="/profile"
            icon={UserCircle}
            title="My profile"
            body="Update your details, contact preferences and email signature."
          />
        </div>
      </section>
    </AppShell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="t-meta">{label}</dt>
      <dd className="tabular-nums font-semibold text-ice">{value}</dd>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  to,
}: {
  label: string;
  value: number;
  icon: typeof Users;
  to: string;
}) {
  return (
    <Link to={to} className="panel p-5 transition-colors hover:border-cyan/60">
      <div className="flex items-center justify-between">
        <p className="t-meta">{label}</p>
        <Icon className="h-4 w-4 text-cyan" />
      </div>
      <p className="mt-3 text-3xl font-bold tabular-nums text-ice">{value}</p>
    </Link>
  );
}

function Tile({
  to,
  icon: Icon,
  title,
  body,
}: {
  to: string;
  icon: typeof Users;
  title: string;
  body: string;
}) {
  return (
    <Link to={to} className="panel flex flex-col gap-2 p-5 transition-colors hover:border-cyan/60">
      <Icon className="h-4 w-4 text-cyan" />
      <p className="text-sm font-semibold text-ice">{title}</p>
      <p className="t-body text-xs">{body}</p>
    </Link>
  );
}
